import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography, Avatar, Dropdown, Space, ConfigProvider, Card, Tag, Button, Modal, Tooltip, theme as antdTheme } from 'antd';
import {
  DownloadOutlined,
  DashboardOutlined,
  SoundOutlined,
  YoutubeOutlined,
  DollarOutlined,
  WalletOutlined,
  FileExcelOutlined,
  BarChartOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SunOutlined,
  SettingOutlined,

  VideoCameraOutlined,
  TeamOutlined,
  FileImageOutlined,
  FileProtectOutlined,
  CheckSquareOutlined,
  MoneyCollectOutlined,
  QrcodeOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  RobotOutlined,
  RiseOutlined,
  FireOutlined,
  HistoryOutlined,
  HomeOutlined,
  FundOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  LinkOutlined,
  CrownOutlined,
  ControlOutlined,
  MessageOutlined,
  ToolOutlined,
  GlobalOutlined,
  NotificationOutlined
} from '@ant-design/icons';

import TaskBanner from './components/TaskBanner';
import VideoTrackerBanner from './components/VideoTrackerBanner';
import HolidayBanner from './components/HolidayBanner';
import NoticeBanner from './components/NoticeBanner';
import FestivalPopup from './components/FestivalPopup';
import GlobalSearch from './components/GlobalSearch';
import NotificationBell from './components/NotificationBell';
import LiveChatNotifier from './components/LiveChatNotifier';
import ChatWidget from './components/ChatWidget';
import AiWidget from './components/AiWidget';
import Login from './pages/Login';
import { hasPerm, getPerms } from './utils/perms';
import { COMPANY_SITE_PERMS, siteMenuKey, siteFromMenuKey, visibleSites } from './utils/companySites';
import api from './utils/api';
import { AudioPlayerProvider } from './components/AudioPlayerContext';
import MiniPlayer from './components/MiniPlayer';

// Pages are statically imported into one bundle. Route-level code-splitting was
// tried but hurt real-world navigation: each menu click had to fetch a separate
// chunk over a high-latency link before the page could even start loading its
// data, so every section showed a multi-second spinner. With a single bundle all
// page code is already in memory, so navigation is instant.
// PosterGallery removed — thumbnails cover this.
// BackupStatus, StaffPerformance, ArtistDashboard, ReleaseCalendar moved into Settings.
// StudioPayments merged into StudioReport.
import VideoEditLog from './pages/VideoEditLog';
import StaffAssignment from './pages/StaffAssignment';
import StaffDashboard from './pages/StaffDashboard';
import Thumbnails from './pages/Thumbnails';
import SongAgreements from './pages/SongAgreements';
import VideoDownloader from './pages/VideoDownloader';
import StaffManagement from './pages/StaffManagement';
import SalaryHisab from './pages/SalaryHisab';
import MySalary from './pages/MySalary';
import Tasks from './pages/Tasks';
import Royalties from './pages/Royalties';
import ClientHub from './pages/ClientHub';
import ClientDashboard from './pages/ClientDashboard';
import QrScan from './pages/QrScan';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import Songs from './pages/Songs';
import Channels from './pages/Channels';
import Income from './pages/Income';
import Expenses from './pages/Expenses';
import ExcelUpload from './pages/ExcelUpload';
import Reports from './pages/Reports';
import Users from './pages/Users';
import StaffTasks from './pages/StaffTasks';
import NoticeBoard from './pages/NoticeBoard';
import AiAudio from './pages/AiAudio';
import SongMetadata from './pages/SongMetadata';
import ActivityLog from './pages/ActivityLog';
import SongTimeline from './pages/SongTimeline';
import StudioReport from './pages/StudioReport';
import MonthlyReport from './pages/MonthlyReport';
import ChannelActivity from './pages/ChannelActivity';
import WorkReport from './pages/WorkReport';
import ClaimLinks from './pages/ClaimLinks';
import StoreLinks from './pages/StoreLinks';
import Trending from './pages/Trending';
import CompanySites from './pages/CompanySites';
import LiveChat from './pages/LiveChat';
import AiCenter from './pages/AiCenter';
import VideoTracker from './pages/VideoTracker';
import SuperAdminPanel from './pages/SuperAdminPanel';
import AgreementGenerator from './pages/AgreementGenerator';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

function formatHour(h) {
  if (h === 0 || h === 24) return '12:00 AM';
  if (h === 12) return '12:00 PM';
  if (h < 12) return `${h}:00 AM`;
  return `${h - 12}:00 PM`;
}

function LiveClock({ user }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const date = now.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <span className="hide-on-mobile" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#667eea22,#764ba222)', padding: '4px 12px', borderRadius: 8, fontSize: 13 }}>
      <ClockCircleOutlined style={{ color: '#667eea' }} />
      <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{time}</span>
      <span style={{ color: '#888', fontSize: 11 }}>{date}</span>
      {user?.staff_id && <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>{user.staff_id}</Tag>}
    </span>
  );
}

// Distance between two lat/lng points in metres (haversine).
function metresBetween(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function OfficeHoursGate({ user, children }) {
  const startH = user.office_hours_start ?? 9;
  const endH = user.office_hours_end ?? 19;
  const gate = user.login_gate || {};
  const officeLat = Number(gate.office_lat || 0);
  const officeLng = Number(gate.office_lng || 0);
  const loginRadius = Number(gate.login_radius || 500);
  const geoEnforced = officeLat !== 0 && officeLng !== 0 && !user.login_anywhere;
  const sundayEnforced = !user.login_sunday;

  const [blocked, setBlocked] = useState(false);
  const [nextOpen, setNextOpen] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  // location gate: 'ok' | 'checking' | 'far' | 'denied'
  const [geoState, setGeoState] = useState(geoEnforced ? 'checking' : 'ok');
  const [geoDist, setGeoDist] = useState(null);
  const farCountRef = useRef(0);   // consecutive genuinely-far readings
  const everOkRef = useRef(false); // was verified inside office at least once this session

  // Time + Sunday gate
  useEffect(() => {
    const check = () => {
      const now = new Date();
      setCurrentTime(now);
      const h = now.getHours();
      let timeAllowed = true;
      if (!(startH === 0 && endH === 24)) {
        timeAllowed = startH < endH ? (h >= startH && h < endH) : (h >= startH || h < endH);
      }
      if (!timeAllowed) {
        setBlocked(true);
        const openAt = new Date(now);
        if (startH > endH) {
          if (h < startH && h >= endH) { openAt.setHours(startH, 0, 0, 0); }
          else { openAt.setDate(openAt.getDate() + 1); openAt.setHours(startH, 0, 0, 0); }
        } else {
          if (h >= endH) openAt.setDate(openAt.getDate() + 1);
          openAt.setHours(startH, 0, 0, 0);
        }
        setNextOpen(openAt.toLocaleString('en-IN', { weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: true }));
      } else {
        setBlocked(false);
      }
    };
    check();
    const timer = setInterval(check, 30000);
    return () => clearInterval(timer);
  }, [startH, endH]);

  // Location gate
  useEffect(() => {
    if (!geoEnforced) { setGeoState('ok'); return; }
    let cancelled = false;
    const locate = () => {
      if (!navigator.geolocation) { setGeoState('denied'); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const d = metresBetween(pos.coords.latitude, pos.coords.longitude, officeLat, officeLng);
          const acc = Number(pos.coords.accuracy) || 0; // reading's uncertainty radius (m)
          setGeoDist(Math.round(d));
          // Account for GPS uncertainty: if the office could plausibly be within
          // the reading's error margin, treat as inside (avoids false "far").
          const effective = Math.max(0, d - acc);
          if (effective <= loginRadius) {
            farCountRef.current = 0;
            everOkRef.current = true;
            setGeoState('ok');
            return;
          }
          // Reading is unreliable (very low accuracy) — ignore it entirely,
          // don't nag the user based on a bad fix (common on laptops/wifi).
          const accLimit = Math.max(1500, loginRadius * 3);
          if (acc > accLimit) return; // keep previous state
          // Genuinely far & reasonably accurate — but require it to persist for a
          // few readings before blocking, so one-off jitter doesn't kick them out.
          farCountRef.current += 1;
          const needed = everOkRef.current ? 4 : 2;
          if (farCountRef.current >= needed) setGeoState('far');
        },
        (err) => {
          if (cancelled) return;
          // Only a real permission denial should block. Timeouts / position-
          // unavailable are transient — keep the previous state.
          if (err && err.code === 1) setGeoState('denied');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    };
    locate();
    const timer = setInterval(locate, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [geoEnforced, officeLat, officeLng, loginRadius]);

  const isSundayBlocked = sundayEnforced && currentTime.getDay() === 0;
  const geoBlocked = geoEnforced && (geoState === 'far' || geoState === 'denied');

  if (!blocked && !isSundayBlocked && !geoBlocked) return children;

  let icon = <ClockCircleOutlined style={{ fontSize: 64, color: '#667eea', marginBottom: 16 }} />;
  let title = 'Office Hours Only';
  let body = (
    <>
      <Text style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>
        Dashboard sirf <b>{formatHour(startH)} — {formatHour(endH)}</b> ke beech access hota hai.
      </Text>
      <Tag color="blue" style={{ fontSize: 14, padding: '6px 16px' }}>Next open: {nextOpen}</Tag>
    </>
  );
  if (isSundayBlocked) {
    icon = <CalendarOutlined style={{ fontSize: 64, color: '#fa8c16', marginBottom: 16 }} />;
    title = 'Sunday — Chhutti';
    body = <Text style={{ fontSize: 16, display: 'block' }}>Aaj <b>Sunday</b> hai — dashboard band hai. Kal khulega. (Admin chahe to aapko Sunday access de sakta hai.)</Text>;
  } else if (geoBlocked) {
    icon = <span style={{ fontSize: 56, display: 'block', marginBottom: 8 }}>📍</span>;
    title = geoState === 'denied' ? 'Location Zaroori Hai' : 'Office Se Bahar';
    body = geoState === 'denied'
      ? <Text style={{ fontSize: 16, display: 'block' }}>Dashboard kholne ke liye <b>location permission</b> deni hogi. Browser me location "Allow" karo aur page refresh karo.</Text>
      : <>
          <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>Aap office se <b>bahar</b> ho. Dashboard sirf office ke <b>{loginRadius >= 1000 ? (loginRadius / 1000) + ' km' : loginRadius + ' m'}</b> ke andar khulta hai.</Text>
          {geoDist != null && <Tag color="red" style={{ fontSize: 14, padding: '6px 16px' }}>Aap ~{geoDist >= 1000 ? (geoDist / 1000).toFixed(1) + ' km' : geoDist + ' m'} door ho</Tag>}
          <div style={{ marginTop: 12 }}><Button onClick={() => window.location.reload()}>Dobara Check Karo</Button></div>
        </>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ maxWidth: 480, borderRadius: 20, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {icon}
        <Title level={3}>{title}</Title>
        {body}
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          {currentTime.toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short', hour12: true })}
        </Text>
      </Card>
    </div>
  );
}

function AppLayout({ user, onLogout, onUserUpdate, dark, onToggleDark }) {
  const [collapsed, setCollapsed] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const isMobile = () => window.innerWidth <= 768;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') setInstallPrompt(null);
    } else {
      setShowInstallHelp(true);
    }
  };

  const role = user.role;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isClient = role === 'client';
  // A user sees ONLY what is granted to them (via permissions or their role's core
  // defaults). Admin sees everything. `can` is the single source of truth.
  const can = (k) => hasPerm(user, k);
  // Company websites: admin ko sab, staff ko sirf jo tick ki gayi hain.
  const canCompanySites = !isClient && (isAdmin || COMPANY_SITE_PERMS.some(k => can(k)));
  // Har site ka apna menu item — click par wahi site nayi tab me khulti hai.
  const mySites = canCompanySites ? visibleSites(isAdmin, getPerms(user)) : [];
  const siteMenuItems = mySites.map(s => ({
    key: siteMenuKey(s),
    icon: <GlobalOutlined style={{ color: s.color }} />,
    label: s.name,
  }));

  const menuItems = [];

  if (isClient) {
    // CMS client: ONLY their own channel payments dashboard (view-only).
    menuItems.push({ key: '/my-payments', icon: <MoneyCollectOutlined />, label: 'My Channels' });
  } else if (isAdmin) {
    // Admin: full menu (all access)
    menuItems.push(
      { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
      { key: '/songs', icon: <SoundOutlined />, label: 'Song Library' },
      { key: '/ai-audio', icon: <RobotOutlined />, label: 'AI Audio' },
      { key: '/channels', icon: <YoutubeOutlined />, label: 'Bainsla Music Channels' },
      { key: '/income', icon: <DollarOutlined />, label: 'Income' },
      { key: '/expenses', icon: <WalletOutlined />, label: 'Expenses' },
      { key: '/excel-upload', icon: <FileExcelOutlined />, label: 'Excel Upload' },
      { key: '/song-agreements', icon: <FileProtectOutlined />, label: 'Song Agreements' },
      { key: '/agreement-generator', icon: <FileProtectOutlined />, label: 'Agreement Generator' },
      { key: '/song-metadata', icon: <FileExcelOutlined />, label: 'Song Metadata' },
      { key: '/reports', icon: <BarChartOutlined />, label: 'Reports' },
      { key: '/client-payments', icon: <MoneyCollectOutlined />, label: 'Client Channels' },
      { key: '/thumbnails', icon: <FileImageOutlined />, label: 'Thumbnails' },
      { key: '/video-downloader', icon: <YoutubeOutlined />, label: 'Video Downloader' },
      { key: '/video-edit-log', icon: <VideoCameraOutlined />, label: 'Video Edit Log' },

      { key: '/users', icon: <UserOutlined />, label: 'Users & Log' },
      { key: '/staff-assignment', icon: <TeamOutlined />, label: 'Staff Channels' },
      { key: '/staff-salary', icon: <DollarOutlined />, label: 'Staff Salary' },
      { key: '/salary-hisab', icon: <DollarOutlined />, label: 'Salary Hisab' },
      { key: '/staff-attendance', icon: <CalendarOutlined />, label: 'Staff Attendance' },
      { key: '/staff-tasks', icon: <CheckSquareOutlined />, label: 'Staff Tasks' },
      { key: '/notice-board', icon: <NotificationOutlined />, label: 'Notice Board' },
      { key: '/claim-links', icon: <LinkOutlined />, label: 'Claim Links' },
      { key: '/store-links', icon: <LinkOutlined />, label: 'Store Links' },
      { key: '/trending', icon: <FireOutlined />, label: 'YouTube Trending' },
      { key: '/video-tracker', icon: <YoutubeOutlined />, label: 'Video Tracker' },
      { key: '/live-chat', icon: <MessageOutlined />, label: 'Live Chat' },
      { key: '/ai-center', icon: <RobotOutlined />, label: 'AI Center' },
      {
        key: 'company-sites-group',
        icon: <GlobalOutlined />,
        label: 'Company Sites',
        children: [
          ...siteMenuItems,
          { key: '/company-sites', icon: <GlobalOutlined />, label: 'Saari Sites (list)' },
        ],
      },
      { type: 'divider' },
      { key: '/song-timeline', icon: <ClockCircleOutlined />, label: 'Song Timeline' },
      { key: '/studio-report', icon: <HomeOutlined />, label: 'Studio Report' },
      { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
    );
    if (role === 'super_admin') {
      menuItems.push(
        { type: 'divider' },
        { key: '/super-admin', icon: <CrownOutlined />, label: '⚡ Super Admin Panel', style: { background: 'linear-gradient(135deg, #ff4d4f22, #ff7a4522)', fontWeight: 'bold' } }
      );
    }
  } else {
    // Non-admin: strictly permission-driven. Each item appears only if granted.
    if (can('dashboard')) menuItems.push({ key: '/', icon: <DashboardOutlined />, label: 'Dashboard' });
    if (can('song_library') || can('audio_upload')) menuItems.push({ key: '/songs', icon: <SoundOutlined />, label: 'Song Library' });
    if (can('ai_audio_upload') || can('ai_audio_download')) menuItems.push({ key: '/ai-audio', icon: <RobotOutlined />, label: 'AI Audio' });
    if (can('youtube_channels')) menuItems.push({ key: '/channels', icon: <YoutubeOutlined />, label: 'Bainsla Music Channels' });
    if (can('income')) menuItems.push({ key: '/income', icon: <DollarOutlined />, label: 'Income' });
    if (can('expenses')) menuItems.push({ key: '/expenses', icon: <WalletOutlined />, label: 'Expenses' });
    if (can('excel_upload')) menuItems.push({ key: '/excel-upload', icon: <FileExcelOutlined />, label: 'Excel Upload' });
    if (can('song_agreements')) menuItems.push({ key: '/song-agreements', icon: <FileProtectOutlined />, label: 'Song Agreements' });
    if (can('agreement_generator')) menuItems.push({ key: '/agreement-generator', icon: <FileProtectOutlined />, label: 'Agreement Generator' });
    if (can('song_metadata')) menuItems.push({ key: '/song-metadata', icon: <FileExcelOutlined />, label: 'Song Metadata' });
    if (can('reports_view')) menuItems.push({ key: '/reports', icon: <BarChartOutlined />, label: 'Reports' });
    // distribution staff get their own (revenue-free) dashboard as core function
    if (role === 'distribution_staff') menuItems.push({ key: '/my-dashboard', icon: <DashboardOutlined />, label: 'My Dashboard' });
    if (can('video_edit_log')) menuItems.push({ key: '/video-edit-log', icon: <VideoCameraOutlined />, label: role === 'video_editor' ? 'My Edit Log' : 'Video Edit Log' });

    if (can('video_download')) menuItems.push({ key: '/video-downloader', icon: <YoutubeOutlined />, label: 'Video Downloader' });
    // Thumbnails: anyone granted view/download (graphic designers must be granted 'thumbnails')
    if (can('thumbnails') || can('thumbnail_download') || can('thumbnail_upload')) {
      menuItems.push({ key: '/thumbnails', icon: <FileImageOutlined />, label: 'Thumbnails' });
    }
    if (can('staff_management')) {
      menuItems.push({ key: '/users', icon: <UserOutlined />, label: 'Users & Staff' });
      menuItems.push({ key: '/notice-board', icon: <NotificationOutlined />, label: 'Notice Board' });
      menuItems.push({ key: '/staff-salary', icon: <DollarOutlined />, label: 'Staff Salary' });
      menuItems.push({ key: '/salary-hisab', icon: <DollarOutlined />, label: 'Salary Hisab' });
      menuItems.push({ key: '/staff-attendance', icon: <CalendarOutlined />, label: 'Staff Attendance' });
    }
    if (can('channel_activity')) menuItems.push({ key: '/channel-activity', icon: <VideoCameraOutlined />, label: 'Channel Activity' });
    menuItems.push({ key: '/work-report', icon: <FileTextOutlined />, label: 'Daily Work Report' });
    if (role === 'youtube_manager' || can('claim_links')) menuItems.push({ key: '/claim-links', icon: <LinkOutlined />, label: 'Claim Links' });
    if (can('store_links')) menuItems.push({ key: '/store-links', icon: <LinkOutlined />, label: 'Store Links' });
    if (can('trending')) menuItems.push({ key: '/trending', icon: <FireOutlined />, label: 'YouTube Trending' });
    if (can('video_tracker')) menuItems.push({ key: '/video-tracker', icon: <YoutubeOutlined />, label: 'Video Tracker' });
    if (can('staff_tasks')) menuItems.push({ key: '/staff-tasks', icon: <CheckSquareOutlined />, label: 'Staff Tasks' });
    if (can('salary_view')) menuItems.push({ key: '/my-salary', icon: <WalletOutlined />, label: 'My Salary' });
    if (!can('staff_tasks')) menuItems.push({ key: '/staff-tasks', icon: <CheckSquareOutlined />, label: 'My Tasks' });
    menuItems.push({ key: '/live-chat', icon: <MessageOutlined />, label: 'Live Chat' });
    if (can('ai_assistant') || can('ai_tools')) menuItems.push({ key: '/ai-center', icon: <RobotOutlined />, label: 'AI Center' });
    siteMenuItems.forEach(item => menuItems.push(item));
    menuItems.push({ key: '/qr-scan', icon: <QrcodeOutlined />, label: 'QR Attendance' });
    menuItems.push({ key: '/settings', icon: <SettingOutlined />, label: 'Settings' });
  }

  // Thumbnails page may be reached by anyone granted thumbnail access (admin always)
  const canThumbnails = isAdmin || can('thumbnails') || can('thumbnail_download') || can('thumbnail_upload');
  // Where to send a user who lacks access to a page (their first allowed menu item, else Settings)
  const home = menuItems[0]?.key || '/settings';

  const userMenu = {
    items: [
      { key: 'profile', label: `${user.full_name} (${user.role})`, disabled: true },
      { type: 'divider' },
      { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true }
    ],
    onClick: ({ key }) => {
      if (key === 'settings') {
        navigate('/settings');
      } else if (key === 'logout') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        onLogout();
      }
    }
  };

  return (
    <>
    <div className="app-bg"><span className="orb o3" /></div>
    <Layout className="app-shell" style={{ minHeight: '100vh', background: 'transparent' }}>
      {/* Mobile overlay backdrop */}
      {isMobile() && !collapsed && (
        <div
          onClick={() => setCollapsed(true)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }}
        />
      )}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={isMobile() ? 0 : 80}
        onBreakpoint={(broken) => setCollapsed(broken)}
        width={250}
        style={{
          background: 'linear-gradient(180deg,#0f0f23 0%,#1a1a3e 50%,#1e1e4a 100%)',
          boxShadow: '2px 0 24px rgba(10,10,40,0.35)',
          overflowY: 'auto',
          ...(isMobile() ? { position: 'fixed', zIndex: 100, height: '100vh', left: collapsed ? -250 : 0, transition: 'left 0.25s ease' } : {})
        }}
      >
        <div style={{ padding: collapsed ? '20px 8px' : '20px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <img src={collapsed ? '/logo192.png' : '/logo-full.png'} alt="Bainsla Music"
            style={{ width: collapsed ? 36 : 150, height: 'auto', borderRadius: 6, display: 'block', margin: '0 auto' }} />
          {!collapsed && <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginTop: 4 }}>Data Dashboard</Text>}
        </div>
        <Menu
          className="sidebar-menu"
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={({ key }) => {
            const site = siteFromMenuKey(key);
            if (site) {
              window.open(site.url, '_blank', 'noopener,noreferrer');
            } else {
              navigate(key);
            }
            if (isMobile()) setCollapsed(true);
          }}
          items={menuItems}
          style={{ background: 'transparent', borderRight: 0, marginTop: 8 }}
        />
        {/* Install App button moved to Settings page */}
      </Sider>

      <Modal title="Install Bainsla Music App" open={showInstallHelp} onCancel={() => setShowInstallHelp(false)} footer={null} width={480}>
        <div style={{ lineHeight: 2 }}>
          <p><b>Android (Chrome):</b></p>
          <ol>
            <li>Tap <b>⋮</b> (3 dots) in Chrome top-right</li>
            <li>Tap <b>"Install app"</b> or <b>"Add to Home Screen"</b></li>
            <li>Tap <b>"Install"</b> — app icon will appear on your home screen</li>
          </ol>
          <p><b>iPhone (Safari):</b></p>
          <ol>
            <li>Tap the <b>Share</b> button (square with arrow)</li>
            <li>Scroll down and tap <b>"Add to Home Screen"</b></li>
            <li>Tap <b>"Add"</b></li>
          </ol>
          <p><b>Desktop (Chrome):</b></p>
          <ol>
            <li>Click the <b>install icon</b> in the address bar (right side)</li>
            <li>Click <b>"Install"</b></li>
          </ol>
          <p style={{ color: '#888', fontSize: 12, marginTop: 16 }}>After installing, the app will open full-screen without the browser bar — just like a native app.</p>
        </div>
      </Modal>
      <Layout style={{ background: 'transparent' }}>
        <Header style={{ background: dark ? 'rgba(22,24,38,0.92)' : 'rgba(255,255,255,0.88)', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 16px rgba(60,70,120,0.10)', height: 64 }}>
          <Space>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              style: { fontSize: 20, cursor: 'pointer' },
              onClick: () => setCollapsed(!collapsed)
            })}
            <img src="/logo192.png" alt="Bainsla Music" style={{ width: 34, height: 34, borderRadius: 6, marginLeft: 12 }} />
            <Text strong style={{ fontSize: 18, marginLeft: 4, fontFamily: 'Poppins, sans-serif' }}>
              <span className="header-title-full">Bainsla Music Data Dashboard</span>
              <span className="header-title-short">Bainsla Music</span>
            </Text>
          </Space>
          <Space size="large">
            <LiveClock user={user} />
            <span className="hide-on-mobile"><GlobalSearch menuItems={menuItems} /></span>
            {!isClient && <LiveChatNotifier />}
            <NotificationBell />
            <Tooltip title={dark ? 'Day Mode' : 'Night / Dark Mode'}>
              <Button shape="circle" aria-label="Toggle dark mode" onClick={onToggleDark}
                icon={dark ? <SunOutlined style={{ color: '#fadb14' }} /> : <MoonOutlined />} />
            </Tooltip>
            <Dropdown menu={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar style={{ background: '#667eea' }} icon={<UserOutlined />} />
                <Text className="hide-on-mobile">{user.full_name}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content className="page-fade content-area" key={location.pathname} style={{ margin: 24, padding: 28, background: dark ? 'rgba(22,24,38,0.9)' : 'rgba(255,255,255,0.88)', borderRadius: 18, minHeight: 360, boxShadow: '0 8px 32px rgba(60,70,120,0.12)' }}>
          {!isClient && <HolidayBanner />}
          <FestivalPopup />
          <NoticeBanner />
          <TaskBanner />
          <VideoTrackerBanner />
          <Routes>
            <Route path="/" element={can('dashboard') ? <Dashboard /> : <Navigate to={home} />} />
            <Route path="/songs" element={(can('song_library') || can('audio_upload')) ? <Songs user={user} /> : <Navigate to={home} />} />
            <Route path="/channels" element={can('youtube_channels') ? <Channels user={user} /> : <Navigate to={home} />} />
            <Route path="/income" element={can('income') ? <Income /> : <Navigate to={home} />} />
            <Route path="/expenses" element={can('expenses') ? <Expenses /> : <Navigate to={home} />} />
            <Route path="/excel-upload" element={can('excel_upload') ? <ExcelUpload /> : <Navigate to={home} />} />
            <Route path="/reports" element={can('reports_view') ? <Reports /> : <Navigate to={home} />} />
            <Route path="/users" element={(isAdmin || can('staff_management')) ? <Users currentUser={user} /> : <Navigate to={home} />} />
            <Route path="/staff-assignment" element={isAdmin ? <StaffAssignment /> : <Navigate to={home} />} />
            <Route path="/my-dashboard" element={(isAdmin || role === 'distribution_staff') ? <StaffDashboard user={user} /> : <Navigate to={home} />} />
            <Route path="/video-edit-log" element={can('video_edit_log') ? <VideoEditLog user={user} /> : <Navigate to={home} />} />

            <Route path="/thumbnails" element={canThumbnails ? <Thumbnails user={user} /> : <Navigate to={home} />} />
            <Route path="/video-downloader" element={can('video_download') ? <VideoDownloader user={user} /> : <Navigate to={home} />} />
            <Route path="/staff-management" element={(isAdmin || can('staff_management')) ? <StaffManagement /> : <Navigate to={home} />} />
            <Route path="/staff-salary" element={(isAdmin || can('staff_management')) ? <StaffManagement view="salary" /> : <Navigate to={home} />} />
            <Route path="/salary-hisab" element={(isAdmin || can('staff_management')) ? <SalaryHisab /> : <Navigate to={home} />} />
            <Route path="/staff-attendance" element={(isAdmin || can('staff_management')) ? <StaffManagement view="attendance" /> : <Navigate to={home} />} />
            <Route path="/my-salary" element={can('salary_view') ? <MySalary /> : <Navigate to={home} />} />
            <Route path="/qr-scan" element={<QrScan />} />
            <Route path="/tasks" element={isAdmin ? <Tasks /> : <Navigate to={home} />} />
            <Route path="/staff-tasks" element={<StaffTasks user={user} />} />
            <Route path="/notice-board" element={(isAdmin || can('staff_management')) ? <NoticeBoard /> : <Navigate to={home} />} />
            <Route path="/royalties" element={isAdmin ? <Royalties /> : <Navigate to={home} />} />
            <Route path="/client-payments" element={isAdmin ? <ClientHub /> : <Navigate to={home} />} />
            <Route path="/my-payments" element={isClient ? <ClientDashboard user={user} /> : <Navigate to={home} />} />
            <Route path="/settings" element={<Settings user={user} onUserUpdate={onUserUpdate} />} />
            <Route path="/song-agreements" element={can('song_agreements') ? <SongAgreements user={user} /> : <Navigate to={home} />} />
            <Route path="/agreement-generator" element={(isAdmin || can('agreement_generator')) ? <AgreementGenerator user={user} /> : <Navigate to={home} />} />
            <Route path="/ai-audio" element={(isAdmin || can('ai_audio_upload') || can('ai_audio_download')) ? <AiAudio user={user} /> : <Navigate to={home} />} />
            <Route path="/song-metadata" element={(isAdmin || can('song_metadata')) ? <SongMetadata user={user} /> : <Navigate to={home} />} />
            <Route path="/activity-log" element={isAdmin ? <ActivityLog /> : <Navigate to={home} />} />
            <Route path="/song-timeline" element={isAdmin ? <SongTimeline /> : <Navigate to={home} />} />
            <Route path="/studio-report" element={isAdmin ? <StudioReport /> : <Navigate to={home} />} />
            <Route path="/monthly-report" element={isAdmin ? <MonthlyReport /> : <Navigate to={home} />} />
            <Route path="/channel-activity" element={(isAdmin || can('channel_activity')) ? <ChannelActivity user={user} /> : <Navigate to={home} />} />
            <Route path="/work-report" element={<WorkReport user={user} />} />
            <Route path="/live-chat" element={isClient ? <Navigate to={home} /> : <LiveChat user={user} />} />
            <Route path="/ai-center" element={(isAdmin || can('ai_assistant') || can('ai_tools')) ? <AiCenter user={user} /> : <Navigate to={home} />} />
            <Route path="/claim-links" element={(isAdmin || role === 'youtube_manager' || can('claim_links')) ? <ClaimLinks user={user} /> : <Navigate to={home} />} />
            <Route path="/store-links" element={(isAdmin || can('store_links')) ? <StoreLinks user={user} /> : <Navigate to={home} />} />
            <Route path="/short-links" element={<Navigate to="/store-links" replace />} />
            <Route path="/trending" element={(isAdmin || can('trending')) ? <Trending /> : <Navigate to={home} />} />
            <Route path="/company-sites" element={canCompanySites ? <CompanySites user={user} /> : <Navigate to={home} />} />
            <Route path="/video-tracker" element={(isAdmin || can('video_tracker')) ? <VideoTracker user={user} /> : <Navigate to={home} />} />
            <Route path="/super-admin" element={role === 'super_admin' ? <SuperAdminPanel user={user} /> : <Navigate to={home} />} />
            <Route path="*" element={<Navigate to={home} />} />
          </Routes>
          {!isClient && !isAdmin && location.pathname !== '/live-chat' && <ChatWidget user={user} />}
          {!isClient && location.pathname !== '/ai-center' && (
            <AiWidget
              title={isAdmin ? 'Ask AI' : 'AI se poochho'}
              suggested={isAdmin
                ? ['How much profit this month?', 'Which client payment is pending?', 'Who is absent today?', 'Top expense categories this month']
                : undefined}
            />
          )}
        </Content>
      </Layout>
    </Layout>
    </>
  );
}

function MaintenanceGate({ isAdmin, children }) {
  const [state, setState] = useState({ on: false, message: '', until: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/settings/maintenance');
        if (!cancelled && res.data) setState(res.data);
      } catch (err) { /* keep last known state */ }
    };
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);

  if (!state.on) return children;

  if (isAdmin) {
    return (
      <>
        <div style={{ background: '#fa8c16', color: '#fff', textAlign: 'center', padding: '6px 12px', fontWeight: 600 }}>
          Maintenance Mode ON — staff & clients ko sirf wait screen dikh raha hai (Settings me band karo)
        </div>
        {children}
      </>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ maxWidth: 480, borderRadius: 20, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <ToolOutlined style={{ fontSize: 64, color: '#fa8c16', marginBottom: 16 }} />
        <Title level={3}>Maintenance chal raha hai</Title>
        <Text style={{ fontSize: 16, display: 'block' }}>
          {state.message || 'Dashboard par kaam ho raha hai. Please wait — thodi der me wapas try karein.'}
        </Text>
        {state.until && <Tag color="orange" style={{ fontSize: 14, padding: '6px 16px', marginTop: 12 }}>Expected: {state.until}</Tag>}
        <div style={{ marginTop: 16 }}><Button onClick={() => window.location.reload()}>Dobara Check Karo</Button></div>
      </Card>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [dark, setDark] = useState(false);
  const themeKey = user?.id ? `dashboard_theme_${user.id}` : null;

  useEffect(() => {
    setDark(themeKey ? localStorage.getItem(themeKey) === 'dark' : false);
  }, [themeKey]);

  useEffect(() => {
    document.body.classList.toggle('theme-dark', dark);
    if (themeKey) localStorage.setItem(themeKey, dark ? 'dark' : 'light');
  }, [dark, themeKey]);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (saved && token) {
      setUser(JSON.parse(saved));
    }
  }, []);

  // Keep the logged-in user's role/permissions in sync with the server so that
  // when an admin changes someone's access it takes effect for them right away
  // (no logout needed). Polls periodically and on window focus.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refreshMe = async () => {
      if (!localStorage.getItem('token')) return;
      try {
        const res = await api.get('/auth/me');
        if (cancelled || !res.data) return;
        const fresh = res.data;
        setUser((prev) => {
          if (!prev) return prev;
          const merged = { ...prev, ...fresh };
          const changed = JSON.stringify(prev.permissions || []) !== JSON.stringify(fresh.permissions || [])
            || prev.role !== fresh.role
            || prev.office_hours_start !== fresh.office_hours_start
            || prev.office_hours_end !== fresh.office_hours_end
            || prev.qr_radius_m !== fresh.qr_radius_m
            || prev.login_anywhere !== fresh.login_anywhere
            || prev.login_sunday !== fresh.login_sunday;
          if (!changed) return prev;
          localStorage.setItem('user', JSON.stringify(merged));
          return merged;
        });
      } catch (err) {
        // 401 => token invalid/expired: force re-login.
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
    };
    refreshMe();
    const id = setInterval(() => { if (!document.hidden) refreshMe(); }, 30000);
    const onFocus = () => refreshMe();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [user?.id]);

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  const isStaff = user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'client';
  const toggleDark = () => setDark(d => !d);

  return (
    <ConfigProvider theme={{ algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm, token: { colorPrimary: '#667eea', borderRadius: 8 } }}>
      <AudioPlayerProvider>
      <Router>
        <MaintenanceGate isAdmin={user.role === 'admin' || user.role === 'super_admin'}>
          {isStaff ? (
            <OfficeHoursGate user={user}>
              <AppLayout user={user} onLogout={() => setUser(null)} onUserUpdate={setUser} dark={dark} onToggleDark={toggleDark} />
            </OfficeHoursGate>
          ) : (
            <AppLayout user={user} onLogout={() => setUser(null)} onUserUpdate={setUser} dark={dark} onToggleDark={toggleDark} />
          )}
        </MaintenanceGate>
        <MiniPlayer />
      </Router>
      </AudioPlayerProvider>
    </ConfigProvider>
  );
}

export default App;
